import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { queryOptions, useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { FaPlus } from "react-icons/fa6";
import ContentPage from "@/components/contentPage";
import PaginationFooter from "@/components/paginationFooter";
import OrganizationCard from "@/components/cards/organizationCard";
import { toast } from "react-toastify";

const organizationListQueryOptions = (api: any, page: number, pageSize: number) =>
  queryOptions({
    queryKey: ["listOrganizations", page, pageSize],
    queryFn: () => api.users.listOrganizations({ page, size: pageSize }),
    throwOnError: true,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

export const Route = createFileRoute("/organizations/")({
  component: OrganizationList,
  loader: ({ context: { queryClient, api } }) =>
    queryClient.prefetchQuery(organizationListQueryOptions(api, 1, 10)),
});

function OrganizationList() {
  const { api } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const [addingNewOrganization, setAddingNewOrganization] = useState(false);
  const [newOrganizationName, setNewOrganizationName] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { isPending, data, isPlaceholderData } = useQuery(
    organizationListQueryOptions(api, page, pageSize),
  );

  const createOrganizationMutation = useMutation({
    mutationFn: (name: string) => api.users.createOrganization({ requestBody: { name } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listOrganizations"] });
      setAddingNewOrganization(false);
      setNewOrganizationName("");
      toast.success("Organization created successfully");
    },
    onError: (error) => {
      toast.error(`Failed to create organization: ${error.message}`);
    },
  });

  const deleteOrganizationMutation = useMutation({
    mutationFn: (organizationId: string) => api.users.deleteOrganization({ organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["listOrganizations"] });
      toast.success("Organization deleted successfully");
    },
    onError: (error) => {
      toast.error(`Failed to delete organization: ${error.message}`);
    },
  });

  const handleNewOrganization = async () => {
    if (newOrganizationName.trim()) {
      createOrganizationMutation.mutate(newOrganizationName);
    }
  };

  const handleDeleteOrganization = async (organizationId: string) => {
    if (window.confirm("Are you sure you want to delete this organization?")) {
      deleteOrganizationMutation.mutate(organizationId);
    }
  };

  if (isPending) return "Loading...";

  return (
    <ContentPage title={"Organizations"}>
      <>
        <div className="grid grid-cols-1 gap-4 pt-2">
          {data.items.map((organization: any) => (
            <OrganizationCard
              key={organization.id}
              organization={organization}
              onDelete={handleDeleteOrganization}
            />
          ))}
          {addingNewOrganization && (
            <div className="mb-6 rounded-md bg-gray-100 p-4">
              <input
                onChange={(e) => setNewOrganizationName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNewOrganization()}
                value={newOrganizationName}
                type={"text"}
                className="form-input mt-1 block w-full rounded text-blue-400"
                placeholder="Organization Name"
              />
              <button
                className="ox-button mt-4 w-full px-2 py-1"
                onClick={handleNewOrganization}
              >
                Save
              </button>
            </div>
          )}
        </div>
        <div className="mt-6">
          <PaginationFooter
            isPreviousData={isPlaceholderData}
            page={page}
            setPage={setPage}
            hasMore={data.items.length === pageSize}
          />
        </div>
        <div className="mb-4 flex">
          <div className={"grow"}></div>
          <button
            className="ox-button w-26 px-2 py-1"
            onClick={() => setAddingNewOrganization(!addingNewOrganization)}
          >
            <FaPlus />
            <span className={"pl-2"}>New Organization</span>
          </button>
        </div>
      </>
    </ContentPage>
  );
}
